import { Canvas, Group, Line } from 'fabric';

import { MetaBullet } from '../fabricClasses/MetaBullet';
import { TextEntity } from '../fabricClasses/TextEntity';

/**
 * Центральная система управления метабуллитами
 * Управляет всеми MetaBullet объектами на canvas
 */
export class MetaBulletSystem {
  private canvas: Canvas;
  private canvasId: string;
  private metaBullets: Map<string, MetaBullet> = new Map();
  private isInitialized = false;
  private isInitializing = false;
  private hasPositionChanges = false;

  // Новые свойства для визуализации границ
  private draggedEntity: TextEntity | null = null;
  private draggedEntityOriginalMetaBullet: MetaBullet | null = null;
  private metaBulletBoundaryRect: Group | null = null;
  private isDragOutOfBounds = false;
  private dragThreshold = 50;

  // Новые свойства для объединения метабуллитов
  private targetMetaBullet: MetaBullet | null = null;
  private targetMetaBulletBoundaryRect: Group | null = null;
  private isOverTargetMetaBullet = false;

  constructor(canvas: Canvas, canvasId: string) {
    this.canvas = canvas;
    this.canvasId = canvasId;

    console.log('🚀 MetaBulletSystem initialized for canvas:', canvasId);
  }

  /**
   * Инициализация системы - настройка обработчиков событий
   */
  initialize(): void {
    if (this.isInitialized) {
      console.warn('⚠️ MetaBulletSystem already initialized');
      return;
    }

    this.setupEventListeners();
    this.isInitialized = true;

    console.log('✅ MetaBulletSystem event listeners setup completed');
  }

  /**
   * Начать процесс инициализации (загрузка данных с бэкенда)
   */
  startInitialization(): void {
    console.log('🔄 Starting MetaBullet system initialization...');
    this.isInitializing = true;
    this.hasPositionChanges = false;
  }

  /**
   * Завершить процесс инициализации и создать бекап если нужно
   */
  finishInitialization(): void {
    console.log('✅ Finishing MetaBullet system initialization...', {
      hasPositionChanges: this.hasPositionChanges,
      metaBulletsCount: this.metaBullets.size,
    });

    this.isInitializing = false;

    // Создаем бекап только если были изменения позиций
    if (this.hasPositionChanges) {
      console.log('💾 Creating single backup after initialization due to position changes');
      this.canvas.fire('metaBullet:backup:needed', {
        reason: 'initialization_completed',
        hasChanges: true,
        metaBulletsCount: this.metaBullets.size,
        timestamp: Date.now(),
      });
    } else {
      console.log('✨ No position changes during initialization - no backup needed');
    }
  }

  /**
   * Отметить что были изменения позиций (для решения о бекапе)
   */
  markPositionChanges(): void {
    this.hasPositionChanges = true;
  }

  /**
   * Проверить находится ли система в процессе инициализации
   */
  get isInInitialization(): boolean {
    return this.isInitializing;
  }

  /**
   * Получить перетаскиваемую сущность (для проверки в TextEntity)
   */
  get currentDraggedEntity(): TextEntity | null {
    return this.draggedEntity;
  }

  /**
   * Проверить находится ли перетаскиваемая сущность за границами (для проверки в TextEntity)
   */
  get isCurrentlyDragOutOfBounds(): boolean {
    return this.isDragOutOfBounds;
  }

  /**
   * Создать новый метабуллит из массива сущностей
   * @param entities - TextEntity объекты которые станут основой метабуллита
   * @param predefinedId - Предопределенный ID метабуллита (для загрузки с бэкенда)
   * @returns ID созданного метабуллита
   */
  createMetaBullet(entities: TextEntity[], predefinedId?: string): string {
    if (entities.length === 0) {
      throw new Error('Cannot create MetaBullet without entities');
    }

    console.log(
      '🆕 Creating MetaBullet from entities:',
      entities.map((e) => e.id),
      predefinedId ? `with predefined ID: ${predefinedId}` : '',
    );

    // Создаем новый метабуллит с первой сущностью
    const metaBullet = new MetaBullet(this.canvasId, entities[0].id, predefinedId);

    // Устанавливаем связь первой сущности с метабуллитом
    entities[0].metaBulletId = metaBullet.id;
    entities[0].entityOrder = 0;

    // Добавляем остальные сущности БЕЗ пересчета позиций
    for (let i = 1; i < entities.length; i++) {
      const entity = entities[i];

      // Устанавливаем связь с метабуллитом
      entity.metaBulletId = metaBullet.id;
      entity.entityOrder = i;

      // Добавляем в массив entityIds
      metaBullet.entityIds.push(entity.id);
    }

    // Пересчитываем позиции ОДИН раз для всех сущностей
    // Но только если не в процессе инициализации
    if (!this.isInitializing) {
      metaBullet.recalculatePositions(true);
    }

    // Сохраняем метабуллит в коллекции
    this.metaBullets.set(metaBullet.id, metaBullet);

    console.log('✅ MetaBullet created:', metaBullet.getDebugInfo());
    return metaBullet.id;
  }

  /**
   * Создать новую текстовую сущность в существующем метабуллите
   * @param metaBulletId - ID метабуллита
   * @param afterEntityId - ID сущности, после которой вставить новую (опционально)
   * @param text - Текст для новой сущности (по умолчанию placeholder)
   * @returns Созданная TextEntity
   */
  createEntityInMetaBullet(
    metaBulletId: string,
    afterEntityId?: string,
    text: string = '/',
  ): TextEntity {
    const metaBullet = this.metaBullets.get(metaBulletId);
    if (!metaBullet) {
      throw new Error(`MetaBullet with id ${metaBulletId} not found`);
    }

    console.log('📝 Creating new entity in MetaBullet:', {
      metaBulletId,
      afterEntityId,
      text,
    });

    // Определяем позицию для новой сущности
    const entities = metaBullet.getEntities();
    let insertPosition = { x: 100, y: 100 }; // Дефолтная позиция
    let insertIndex = entities.length; // По умолчанию в конец

    if (afterEntityId) {
      const afterEntity = entities.find((e) => e.id === afterEntityId);
      if (afterEntity) {
        const afterIndex = entities.indexOf(afterEntity);
        insertIndex = afterIndex + 1;

        // Позиция новой сущности под указанной
        insertPosition = {
          x: afterEntity.left || 100,
          y:
            (afterEntity.top || 100) + (afterEntity.height || 0) + (afterEntity.marginBottom || 16),
        };
      }
    } else if (entities.length > 0) {
      // Если не указано после какой сущности, то в конец
      const lastEntity = entities[entities.length - 1];
      insertPosition = {
        x: lastEntity.left || 100,
        y: (lastEntity.top || 100) + (lastEntity.height || 0) + (lastEntity.marginBottom || 16),
      };
    }

    // Создаем новую сущность
    const newEntity = new TextEntity(text, {
      entityType: 'paragraph',
      metaBulletId,
      entityOrder: insertIndex,
      left: insertPosition.x,
      top: insertPosition.y,
      width: 200, // Временная ширина
    });

    // Добавляем на canvas
    this.canvas.add(newEntity);

    // Добавляем в метабуллит
    metaBullet.addEntity(newEntity, insertIndex);

    console.log('✅ New entity created:', newEntity.getDebugInfo());
    return newEntity;
  }

  /**
   * Получить метабуллит по ID
   */
  getMetaBullet(metaBulletId: string): MetaBullet | undefined {
    return this.metaBullets.get(metaBulletId);
  }

  /**
   * Получить все метабуллиты
   */
  getAllMetaBullets(): MetaBullet[] {
    return Array.from(this.metaBullets.values());
  }

  /**
   * Найти метабуллит по ID сущности
   */
  findMetaBulletByEntityId(entityId: string): MetaBullet | undefined {
    for (const metaBullet of this.metaBullets.values()) {
      if (metaBullet.entityIds.includes(entityId)) {
        return metaBullet;
      }
    }
    return undefined;
  }

  /**
   * Удалить метабуллит и все его сущности
   */
  removeMetaBullet(metaBulletId: string): void {
    const metaBullet = this.metaBullets.get(metaBulletId);
    if (!metaBullet) return;

    console.log('🗑️ Removing MetaBullet:', metaBulletId);

    // Удаляем все сущности с canvas
    const entities = metaBullet.getEntities();
    entities.forEach((entity) => {
      this.canvas.remove(entity);
    });

    // Удаляем метабуллит из коллекции
    this.metaBullets.delete(metaBulletId);

    console.log('✅ MetaBullet removed:', metaBulletId);
  }

  /**
   * Очистка системы
   */
  destroy(): void {
    console.log('💥 Destroying MetaBulletSystem');

    // Удаляем все обработчики событий
    this.removeEventListeners();

    // Очищаем коллекцию метабуллитов
    this.metaBullets.clear();

    this.isInitialized = false;
  }

  /**
   * Настройка обработчиков событий
   */
  private setupEventListeners(): void {
    // Обработка событий от TextEntity
    this.canvas.on('metaBullet:entity:changed', this.handleEntityChanged.bind(this));

    // Обработка удаления объектов
    this.canvas.on('object:removed', this.handleObjectRemoved.bind(this));

    // Новые обработчики для drag & drop
    this.canvas.on('mouse:down', this.handleMouseDown.bind(this));
    this.canvas.on('object:moving', this.handleObjectMoving.bind(this));
    this.canvas.on('mouse:up', this.handleMouseUp.bind(this));

    console.debug('🎧 Event listeners attached to canvas');
  }

  /**
   * Удаление обработчиков событий
   */
  private removeEventListeners(): void {
    this.canvas.off('metaBullet:entity:changed', this.handleEntityChanged.bind(this));
    this.canvas.off('object:removed', this.handleObjectRemoved.bind(this));

    // Удаляем новые обработчики
    this.canvas.off('mouse:down', this.handleMouseDown.bind(this));
    this.canvas.off('object:moving', this.handleObjectMoving.bind(this));
    this.canvas.off('mouse:up', this.handleMouseUp.bind(this));
  }

  /**
   * Обработчик изменений в TextEntity
   */
  private handleEntityChanged(event: {
    entityId: string;
    metaBulletId: string;
    eventType: string;
    data?: unknown;
  }): void {
    const { entityId, metaBulletId, eventType, data } = event;

    console.debug('📨 Entity changed event:', {
      entityId,
      metaBulletId,
      eventType,
      data,
    });

    // ВАЖНО: Игнорируем события от сущности которая находится за границами во время drag'а
    // Это предотвращает ненужную рекалькуляцию старого метабуллита
    if (
      this.draggedEntity &&
      this.draggedEntity.id === entityId &&
      this.isDragOutOfBounds &&
      eventType === 'entity_modified'
    ) {
      console.debug('🚫 Ignoring entity_modified event for dragged entity outside bounds:', {
        entityId,
        isDragOutOfBounds: this.isDragOutOfBounds,
      });
      return;
    }

    const metaBullet = this.metaBullets.get(metaBulletId);
    if (!metaBullet) {
      console.warn('⚠️ MetaBullet not found for entity event:', metaBulletId);
      return;
    }

    console.debug(eventType, ' eventType !!!!!!!!!!!!!!!!!!!');

    // Обрабатываем разные типы событий
    switch (eventType) {
      case 'text_changed':
      case 'type_changed':
      case 'size_changed':
        // Пересчитываем позиции при изменении текста или типа
        metaBullet.recalculatePositions(true);
        break;

      case 'level_changed':
        // При изменении уровня буллита тоже пересчитываем
        metaBullet.recalculatePositions(true);
        break;

      case 'position_changed':
        // При перемещении объекта пересчитываем позиции всего метабуллита
        console.debug('🔄 Recalculating MetaBullet positions due to drag & drop:', {
          metaBulletId,
          entityId,
          newPosition: data,
        });
        metaBullet.recalculatePositions(true);
        break;

      case 'entity_modified':
        // Объединенное событие - любые изменения в сущности (текст, позиция, размеры)
        console.debug('🔄 Recalculating MetaBullet positions due to entity modification:', {
          metaBulletId,
          entityId,
          changes: data,
        });
        metaBullet.recalculatePositions(true);
        break;
    }
  }

  /**
   * Обработчик удаления объектов с canvas
   */
  private handleObjectRemoved(event: { target: unknown }): void {
    const removedObject = event.target;

    // Проверяем, является ли удаленный объект TextEntity
    if (TextEntity.isTextEntity(removedObject) && removedObject.metaBulletId) {
      console.debug('🗑️ TextEntity removed from canvas:', removedObject.id);

      const metaBullet = this.metaBullets.get(removedObject.metaBulletId);
      if (metaBullet) {
        metaBullet.removeEntity(removedObject.id);

        // Если метабуллит стал пустым, удаляем его
        if (metaBullet.entityIds.length === 0) {
          this.metaBullets.delete(removedObject.metaBulletId);
          console.debug('🗑️ Empty MetaBullet removed:', removedObject.metaBulletId);
        }
      }
    }
  }

  /**
   * Обработчик начала перетаскивания
   */
  private handleMouseDown(event: { target?: unknown }): void {
    const target = event.target;

    if (TextEntity.isTextEntity(target) && target.metaBulletId) {
      this.draggedEntity = target;
      this.draggedEntityOriginalMetaBullet = this.metaBullets.get(target.metaBulletId) || null;
      this.isDragOutOfBounds = false;

      console.debug('🖱️ Started dragging TextEntity:', {
        entityId: target.id,
        metaBulletId: target.metaBulletId,
      });
    }
  }

  /**
   * Обработчик перемещения объекта
   */
  private handleObjectMoving(event: { target?: unknown }): void {
    const target = event.target;

    if (!TextEntity.isTextEntity(target) || !this.draggedEntity || target !== this.draggedEntity) {
      return;
    }

    if (!this.draggedEntityOriginalMetaBullet) {
      return;
    }

    // Проверяем расстояние от оригинального метабуллита
    const distance = this.calculateDistanceFromMetaBullet(
      target,
      this.draggedEntityOriginalMetaBullet,
    );

    // ВАЖНО: Сначала обновляем флаг isDragOutOfBounds
    const wasOutOfBounds = this.isDragOutOfBounds;
    const isNowOutOfBounds = distance > this.dragThreshold;

    // Обновляем состояние ДО показа/скрытия рамок
    this.isDragOutOfBounds = isNowOutOfBounds;

    // Логика показа рамки оригинального метабуллита
    if (isNowOutOfBounds && !wasOutOfBounds) {
      // Показываем границы метабуллита
      this.showMetaBulletBoundary(this.draggedEntityOriginalMetaBullet);

      console.debug('🚨 Entity dragged out of bounds, showing boundary:', {
        entityId: target.id,
        distance,
        threshold: this.dragThreshold,
      });
    } else if (!isNowOutOfBounds && wasOutOfBounds) {
      // Скрываем границы метабуллита
      this.hideMetaBulletBoundary();

      console.debug('✅ Entity returned to bounds, hiding boundary:', {
        entityId: target.id,
        distance,
      });
    }

    // Проверяем возможность объединения с другими метабуллитами
    this.checkForMetaBulletMerge(target);
  }

  /**
   * Обработчик завершения перетаскивания
   */
  private handleMouseUp(): void {
    if (!this.draggedEntity || !this.draggedEntityOriginalMetaBullet) {
      return;
    }

    console.debug('🖱️ Finished dragging TextEntity:', {
      entityId: this.draggedEntity.id,
      isDragOutOfBounds: this.isDragOutOfBounds,
      isOverTargetMetaBullet: this.isOverTargetMetaBullet,
    });

    // Если сущность находится над другим метабуллитом, объединяем их
    if (this.isOverTargetMetaBullet && this.targetMetaBullet) {
      this.mergeEntityIntoMetaBullet(
        this.draggedEntity,
        this.draggedEntityOriginalMetaBullet,
        this.targetMetaBullet,
      );
    }
    // Если сущность была вытащена за границы, создаем новый метабуллет
    else if (this.isDragOutOfBounds) {
      this.createNewMetaBulletFromDraggedEntity(
        this.draggedEntity,
        this.draggedEntityOriginalMetaBullet,
      );
    }

    // Скрываем все границы и очищаем состояние
    this.hideMetaBulletBoundary();
    this.hideTargetMetaBulletBoundary();
    this.resetDragState();
  }

  /**
   * Проверить возможность объединения с другими метабуллитами
   */
  private checkForMetaBulletMerge(entity: TextEntity): void {
    // Ищем метабуллит в который может "заехать" перетаскиваемая сущность
    const potentialTargetMetaBullet = this.findMetaBulletUnderEntity(entity);

    // Если нашли новый целевой метабуллит
    if (
      potentialTargetMetaBullet &&
      potentialTargetMetaBullet.id !== this.draggedEntityOriginalMetaBullet?.id
    ) {
      if (
        !this.isOverTargetMetaBullet ||
        this.targetMetaBullet?.id !== potentialTargetMetaBullet.id
      ) {
        // Скрываем предыдущую границу целевого метабуллита
        this.hideTargetMetaBulletBoundary();

        // Показываем новую границу
        this.targetMetaBullet = potentialTargetMetaBullet;
        this.showTargetMetaBulletBoundary(potentialTargetMetaBullet);
        this.isOverTargetMetaBullet = true;

        console.debug('🎯 Entity over target MetaBullet:', {
          entityId: entity.id,
          targetMetaBulletId: potentialTargetMetaBullet.id,
        });
      }
    }
    // Если сущность больше не над целевым метабуллитом
    else if (this.isOverTargetMetaBullet && !potentialTargetMetaBullet) {
      this.hideTargetMetaBulletBoundary();
      this.targetMetaBullet = null;
      this.isOverTargetMetaBullet = false;

      console.debug('❌ Entity left target MetaBullet area');
    }
  }

  /**
   * Найти метабуллит под перетаскиваемой сущностью
   */
  private findMetaBulletUnderEntity(entity: TextEntity): MetaBullet | null {
    const entityCenterX = (entity.left || 0) + (entity.width || 0) / 2;
    const entityCenterY = (entity.top || 0) + (entity.height || 0) / 2;

    // Проверяем все метабуллиты кроме оригинального
    for (const metaBullet of this.metaBullets.values()) {
      if (metaBullet.id === this.draggedEntityOriginalMetaBullet?.id) {
        continue; // Пропускаем оригинальный метабуллит
      }

      // Получаем границы метабуллита
      const entities = metaBullet.getEntities();
      if (entities.length === 0) continue;

      let minX = Infinity,
        minY = Infinity,
        maxX = -Infinity,
        maxY = -Infinity;

      entities.forEach((e) => {
        const left = e.left || 0;
        const top = e.top || 0;
        const right = left + (e.width || 0);
        const bottom = top + (e.height || 0);

        minX = Math.min(minX, left);
        minY = Math.min(minY, top);
        maxX = Math.max(maxX, right);
        maxY = Math.max(maxY, bottom);
      });

      // Добавляем отступ для более удобного "заезжания"
      const padding = 60;
      minX -= padding;
      minY -= padding;
      maxX += padding;
      maxY += padding;

      // Проверяем попадает ли центр сущности в область метабуллита
      if (
        entityCenterX >= minX &&
        entityCenterX <= maxX &&
        entityCenterY >= minY &&
        entityCenterY <= maxY
      ) {
        return metaBullet;
      }
    }

    return null;
  }

  /**
   * Показать границы целевого метабуллита (зеленые)
   */
  private showTargetMetaBulletBoundary(metaBullet: MetaBullet): void {
    // Сначала скрываем предыдущую границу если есть
    this.hideTargetMetaBulletBoundary();

    const entities = metaBullet.getEntities();

    if (entities.length === 0) {
      return;
    }

    // Вычисляем границы метабуллита
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    entities.forEach((entity) => {
      const left = entity.left || 0;
      const top = entity.top || 0;
      const right = left + (entity.width || 0);
      const bottom = top + (entity.height || 0);

      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    });

    // Добавляем отступы
    const padding = 60;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    // Создаем четыре линии для рамки (зеленые)
    const strokeOptions = {
      stroke: '#28a745', // Зеленый цвет для целевого метабуллита
      strokeWidth: 2,
      strokeDashArray: [8, 4], // Другой паттерн пунктира
      selectable: false,
      evented: false,
    };

    const topLine = new Line([minX, minY, maxX, minY], strokeOptions);
    const rightLine = new Line([maxX, minY, maxX, maxY], strokeOptions);
    const bottomLine = new Line([maxX, maxY, minX, maxY], strokeOptions);
    const leftLine = new Line([minX, maxY, minX, minY], strokeOptions);

    // Создаем группу из линий
    this.targetMetaBulletBoundaryRect = new Group([topLine, rightLine, bottomLine, leftLine], {
      selectable: false,
      evented: false,
      excludeFromExport: true,
    });

    // Добавляем на canvas
    this.canvas.add(this.targetMetaBulletBoundaryRect);
    this.canvas.requestRenderAll();

    console.debug('🟢 Target MetaBullet boundary shown:', {
      metaBulletId: metaBullet.id,
      bounds: { minX, minY, maxX, maxY },
    });
  }

  /**
   * Скрыть границы целевого метабуллита
   */
  private hideTargetMetaBulletBoundary(): void {
    if (this.targetMetaBulletBoundaryRect) {
      this.canvas.remove(this.targetMetaBulletBoundaryRect);
      this.targetMetaBulletBoundaryRect = null;
      this.canvas.requestRenderAll();

      console.debug('🟢 Target MetaBullet boundary hidden');
    }
  }

  /**
   * Объединить сущность с целевым метабуллитом
   */
  private mergeEntityIntoMetaBullet(
    entity: TextEntity,
    originalMetaBullet: MetaBullet,
    targetMetaBullet: MetaBullet,
  ): void {
    console.log('🤝 Merging entity into target MetaBullet:', {
      entityId: entity.id,
      originalMetaBulletId: originalMetaBullet.id,
      targetMetaBulletId: targetMetaBullet.id,
    });

    // ВАЖНО: Сначала удаляем сущность из оригинального метабуллита
    // Это предотвращает участие сущности в рекалькуляции старого метабуллита
    originalMetaBullet.removeEntity(entity.id);

    // Временно сбрасываем связь с оригинальным метабуллитом
    const originalMetaBulletId = entity.metaBulletId;
    entity.metaBulletId = '';
    entity.entityOrder = 0;

    // Добавляем сущность в целевой метабуллит в конец
    targetMetaBullet.addEntity(entity);

    console.log('✅ Entity merged into target MetaBullet:', {
      entityId: entity.id,
      newMetaBulletId: targetMetaBullet.id,
      originalMetaBulletId: originalMetaBulletId,
    });

    // Если оригинальный метабуллет стал пустым, удаляем его
    if (originalMetaBullet.entityIds.length === 0) {
      this.metaBullets.delete(originalMetaBullet.id);
      console.log('🗑️ Original MetaBullet removed (empty):', originalMetaBullet.id);
    }

    // Отправляем событие о создании бекапа
    this.canvas.fire('metaBullet:backup:needed', {
      reason: 'entity_merged_into_metabullet',
      targetMetaBulletId: targetMetaBullet.id,
      originalMetaBulletId: originalMetaBulletId,
      entityId: entity.id,
      timestamp: Date.now(),
    });
  }

  /**
   * Вычислить расстояние от сущности до границ её метабуллита
   */
  private calculateDistanceFromMetaBullet(entity: TextEntity, metaBullet: MetaBullet): number {
    const entities = metaBullet.getEntities().filter((e) => e.id !== entity.id); // Исключаем саму перетаскиваемую сущность

    if (entities.length === 0) {
      return 0; // Если это единственная сущность в метабуллите
    }

    // Находим границы остальных сущностей метабуллита
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    entities.forEach((e) => {
      const left = e.left || 0;
      const top = e.top || 0;
      const right = left + (e.width || 0);
      const bottom = top + (e.height || 0);

      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    });

    // Получаем координаты центра перетаскиваемой сущности
    const entityLeft = entity.left || 0;
    const entityTop = entity.top || 0;
    const entityCenterX = entityLeft + (entity.width || 0) / 2;
    const entityCenterY = entityTop + (entity.height || 0) / 2;

    // Если сущность находится внутри области метабуллита, возвращаем 0
    if (
      entityCenterX >= minX &&
      entityCenterX <= maxX &&
      entityCenterY >= minY &&
      entityCenterY <= maxY
    ) {
      return 0;
    }

    // Вычисляем минимальное расстояние до ближайшей границы
    const distances = [];

    // Если вышли влево от метабуллита
    if (entityCenterX < minX) {
      distances.push(minX - entityCenterX);
    }

    // Если вышли вправо от метабуллита
    if (entityCenterX > maxX) {
      distances.push(entityCenterX - maxX);
    }

    // Если вышли вверх от метабуллита
    if (entityCenterY < minY) {
      distances.push(minY - entityCenterY);
    }

    // Если вышли вниз от метабуллита
    if (entityCenterY > maxY) {
      distances.push(entityCenterY - maxY);
    }

    // Возвращаем минимальное расстояние (насколько далеко от ближайшей границы)
    return distances.length > 0 ? Math.min(...distances) : 0;
  }

  /**
   * Показать границы метабуллита
   */
  private showMetaBulletBoundary(metaBullet: MetaBullet): void {
    // Сначала скрываем предыдущую границу если есть
    this.hideMetaBulletBoundary();

    const entities = metaBullet.getEntities().filter((e) => e.id !== this.draggedEntity?.id);

    if (entities.length === 0) {
      return;
    }

    // Вычисляем границы метабуллита
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity;

    entities.forEach((entity) => {
      const left = entity.left || 0;
      const top = entity.top || 0;
      const right = left + (entity.width || 0);
      const bottom = top + (entity.height || 0);

      minX = Math.min(minX, left);
      minY = Math.min(minY, top);
      maxX = Math.max(maxX, right);
      maxY = Math.max(maxY, bottom);
    });

    // Добавляем отступы
    const padding = 60;
    minX -= padding;
    minY -= padding;
    maxX += padding;
    maxY += padding;

    // Создаем четыре линии для рамки
    const strokeOptions = {
      stroke: '#ff6b6b',
      strokeWidth: 2,
      strokeDashArray: [5, 5],
      selectable: false,
      evented: false,
    };

    const topLine = new Line([minX, minY, maxX, minY], strokeOptions);
    const rightLine = new Line([maxX, minY, maxX, maxY], strokeOptions);
    const bottomLine = new Line([maxX, maxY, minX, maxY], strokeOptions);
    const leftLine = new Line([minX, maxY, minX, minY], strokeOptions);

    // Создаем группу из линий
    this.metaBulletBoundaryRect = new Group([topLine, rightLine, bottomLine, leftLine], {
      selectable: false,
      evented: false,
      excludeFromExport: true,
    });

    // Добавляем на canvas
    this.canvas.add(this.metaBulletBoundaryRect);
    this.canvas.requestRenderAll();

    console.debug('🔲 MetaBullet boundary shown:', {
      metaBulletId: metaBullet.id,
      bounds: { minX, minY, maxX, maxY },
    });
  }

  /**
   * Скрыть границы метабуллита
   */
  private hideMetaBulletBoundary(): void {
    if (this.metaBulletBoundaryRect) {
      this.canvas.remove(this.metaBulletBoundaryRect);
      this.metaBulletBoundaryRect = null;
      this.canvas.requestRenderAll();

      console.debug('🔲 MetaBullet boundary hidden');
    }
  }

  /**
   * Создать новый метабуллет из вытащенной сущности
   */
  private createNewMetaBulletFromDraggedEntity(
    entity: TextEntity,
    originalMetaBullet: MetaBullet,
  ): void {
    console.log('🆕 Creating new MetaBullet from dragged entity:', {
      entityId: entity.id,
      originalMetaBulletId: originalMetaBullet.id,
    });

    // ВАЖНО: Сначала удаляем сущность из оригинального метабуллита
    // Это предотвращает участие сущности в рекалькуляции старого метабуллита
    originalMetaBullet.removeEntity(entity.id);

    // Сбрасываем связь с оригинальным метабуллитом у сущности
    entity.metaBulletId = '';
    entity.entityOrder = 0;

    // Теперь создаем новый метабуллет с этой сущностью
    const newMetaBulletId = this.createMetaBullet([entity]);

    console.log('✅ New MetaBullet created from drag:', {
      newMetaBulletId,
      entityId: entity.id,
      originalMetaBulletId: originalMetaBullet.id,
    });

    // Если оригинальный метабуллет стал пустым, удаляем его
    if (originalMetaBullet.entityIds.length === 0) {
      this.metaBullets.delete(originalMetaBullet.id);
      console.log('🗑️ Original MetaBullet removed (empty):', originalMetaBullet.id);
    }

    // Отправляем событие о создании бекапа
    this.canvas.fire('metaBullet:backup:needed', {
      reason: 'entity_extracted_to_new_metabullet',
      newMetaBulletId,
      originalMetaBulletId: originalMetaBullet.id,
      entityId: entity.id,
      timestamp: Date.now(),
    });
  }

  /**
   * Сбросить состояние перетаскивания
   */
  private resetDragState(): void {
    this.draggedEntity = null;
    this.draggedEntityOriginalMetaBullet = null;
    this.isDragOutOfBounds = false;

    // Сбрасываем состояние объединения
    this.targetMetaBullet = null;
    this.isOverTargetMetaBullet = false;
  }

  /**
   * Получить статистику системы для отладки
   */
  getDebugInfo(): {
    canvasId: string;
    isInitialized: boolean;
    metaBulletsCount: number;
    totalEntities: number;
    metaBullets: string[];
  } {
    const metaBullets = Array.from(this.metaBullets.values());

    return {
      canvasId: this.canvasId,
      isInitialized: this.isInitialized,
      metaBulletsCount: metaBullets.length,
      totalEntities: metaBullets.reduce((sum, mb) => sum + mb.entityIds.length, 0),
      metaBullets: metaBullets.map((mb) => mb.getDebugInfo()),
    };
  }
}
